import type {
  UnitInfoLMScreenInterface,
  LMScreenFamily,
} from 'store/units/types';
import { BaseUnitConfig } from 'unitData/BaseUnitConfig';
import * as LMScreenConfig from 'unitData/LinearMotionScreen/LinearMotionScreenData';
import type {
  ScreeningMediaInterface,
  LinearMotionScreenDeckMapInterface,
} from './LinearMotionScreenTypes';

/**
 * Samma som för circular
 */
export enum ScreenModels {
  WS6000 = 'WS6000',
  WS6000H = 'WS6000H',
  WN4000WN5000 = 'WN4000_WN5000',
  WN6000 = 'WN6000',
  WN7000H = 'WN7000H',
  WN8000 = 'WN8000',
  WX6000 = 'WX6000',
  WX6500 = 'WX6500',
  WX7000 = 'WX7000',
  WX8500 = 'WX8500',
  WI8500 = 'WI8500',
  WX1000WM = 'WX1000 (WM)',
  WX1000HW = 'WX1000 (HW)',
  WP1000 = 'WP1000',
  SCREENEX_40D = 'Screenex 40D',
  SCREENEX_60D = 'Screenex 60D',
  SCREENEX_65D = 'Screenex 65D',
  SCREENEX_80D = 'Screenex 80D',
}

/**
 * Exakt samma som för circular
 */
export const ScreeningMediaDataNormalCalcSL: ScreeningMediaInterface[] = [
  {
    model: ScreenModels.WS6000,
    description: 'Duratuff',
    material: 'Rubber',
    type: 'Self-supporting',
    function: 'Scalping',
    separationSize: {
      metric: '44 - 120',
      imperial: '1.26, 1.73 - 4.72',
    },
    maxFeedSize: {
      metric: '150 - 300',
      imperial: '5.91 - 11.8',
    },
  },
  {
    model: ScreenModels.WS6000H,
    description: 'Ribbtuff',
    material: 'Rubber',
    type: 'Self-supporting',
    function: 'Scalping',
    separationSize: {
      metric: '44 - 120',
      imperial: '1.73 - 4.72',
    },
    maxFeedSize: {
      metric: '250 - 400',
      imperial: '9.84 - 15.8',
    },
  },
  {
    model: ScreenModels.WN4000WN5000,
    description: '',
    material: 'Rubber',
    type: 'Modular',
    function: 'Anti-blinding',
    separationSize: {
      metric: '2 - 25',
      imperial: '0.079 - 0.98',
    },
    maxFeedSize: {
      metric: '20 - 50',
      imperial: '0.787 - 1.97',
    },
  },
  {
    model: ScreenModels.WN6000,
    description: '',
    material: 'Rubber',
    type: 'Modular',
    function: 'Standard',
    separationSize: {
      metric: '8 - 90',
      imperial: '0.315 - 3.54',
    },
    maxFeedSize: {
      metric: '50 - 200',
      imperial: '1.97 - 7.87',
    },
  },
  {
    model: ScreenModels.WN7000H,
    description: '',
    material: 'Rubber',
    type: 'Modular',
    function: 'Heavy duty',
    separationSize: {
      metric: '8 - 90',
      imperial: '0.315 - 3.54',
    },
    maxFeedSize: {
      metric: '50 - 200',
      imperial: '1.97 - 7.87',
    },
  },
  {
    model: ScreenModels.WN8000,
    description: '',
    material: 'PU',
    type: 'Modular',
    function: 'Wet (washing)',
    separationSize: {
      metric: '1 - 34',
      imperial: '0.039 - 1.34',
    },
    maxFeedSize: {
      metric: '20 - 125',
      imperial: '0.787 - 4.92',
    },
  },
  {
    model: ScreenModels.WX6000,
    description: 'Crosscord',
    material: 'Rubber',
    type: 'Tensioned',
    function: 'Standard',
    separationSize: {
      metric: '5 - 90',
      imperial: '0.16 - 3.54',
    },
    maxFeedSize: {
      metric: '30 - 200',
      imperial: '1.18 - 7.87',
    },
  },
  {
    model: ScreenModels.WX6500,
    description: '',
    material: 'Rubber',
    type: 'Tensioned',
    function: 'Accuracy',
    separationSize: {
      metric: '2 - 32',
      imperial: '0.079 - 1.26',
    },
    maxFeedSize: {
      metric: '30 - 70',
      imperial: '1.18 - 2.76',
    },
  },
  {
    model: ScreenModels.WX7000,
    description: 'Clearflex',
    material: 'PU',
    type: 'Tensioned',
    function: 'Anti-blinding',
    separationSize: {
      metric: '2 - 16',
      imperial: '0.079 - 0.623',
    },
    maxFeedSize: {
      metric: '20 - 30',
      imperial: '0.787 - 1.18',
    },
  },
  {
    model: ScreenModels.WX8500,
    description: 'Crosscord',
    material: 'PU',
    type: 'Tensioned',
    function: 'Wet (washing)',
    separationSize: {
      metric: '1 - 45',
      imperial: '0.039 - 1.77',
    },
    maxFeedSize: {
      metric: '20 - 125',
      imperial: '0.787 - 4.92',
    },
  },
  {
    model: ScreenModels.WI8500,
    description: '',
    material: 'PU',
    type: 'Modular',
    function: 'Wet/Dry',
    separationSize: {
      metric: '1 - 70',
      imperial: '0.039 - 2.76',
    },
    maxFeedSize: {
      metric: '20 - 150',
      imperial: '0.787 - 5.91',
    },
  },
  {
    model: ScreenModels.WX1000WM,
    description: 'Wire mesh',
    material: 'Steel',
    type: 'Tensioned',
    function: 'Accuracy',
    separationSize: {
      metric: '1 - 90',
      imperial: '0.039 - 3.54',
    },
    maxFeedSize: {
      metric: '20 - 120',
      imperial: '0.787 - 4.72',
    },
  },
  {
    model: ScreenModels.WX1000HW,
    description: 'Harp-wire',
    material: 'Steel',
    type: 'Tensioned',
    function: 'Anti-blinding',
    separationSize: {
      metric: '1 - 16',
      imperial: '0.039 - 0.63',
    },
    maxFeedSize: {
      metric: '10 - 30',
      imperial: '0.394 - 1.18',
    },
  },
  {
    model: ScreenModels.WP1000,
    description: 'Steel plate',
    material: 'Steel',
    type: '',
    function: 'Scalping',
    separationSize: {
      metric: '25 - 140',
      imperial: '0.98 - 5.51',
    },
    maxFeedSize: {
      metric: '200 - 600',
      imperial: '7.87 - 23.6',
    },
  },
  {
    model: ScreenModels.SCREENEX_40D,
    description: '',
    material: 'Rubber',
    type: 'Modular',
    function: 'Anti-Blinding',
    separationSize: {
      metric: '0.6 - 20',
      imperial: '0.0236 - 0.787',
    },
    maxFeedSize: {
      metric: '40',
      imperial: '1.57',
    },
  },
  {
    model: ScreenModels.SCREENEX_60D,
    description: '',
    material: 'Rubber',
    type: 'Modular',
    function: 'Heavy Duty',
    separationSize: {
      metric: '0.5 - 250',
      imperial: '0.0197 - 9.84',
    },
    maxFeedSize: {
      metric: '600',
      imperial: '23.6',
    },
  },
  {
    model: ScreenModels.SCREENEX_65D,
    description: '',
    material: 'PU',
    type: 'Modular',
    function: 'Anti-Blinding',
    separationSize: {
      metric: '0.5 - 20',
      imperial: '0.0197 - 0.787',
    },
    maxFeedSize: {
      metric: '40',
      imperial: '1.57',
    },
  },
  {
    model: ScreenModels.SCREENEX_80D,
    description: '',
    material: 'PU',
    type: 'Modular',
    function: 'Abrasion',
    separationSize: {
      metric: '0.1 - 250',
      imperial: '0.00394 - 9.84',
    },
    maxFeedSize: {
      metric: '600',
      imperial: '23.6',
    },
  },
];
export const ScreeningMediaDataNormalCalcKL: ScreeningMediaInterface[] = [
  {
    model: ScreenModels.WS6000,
    description: 'Duratuff',
    material: 'Rubber',
    type: 'Self-supporting',
    function: 'Scalping',
    separationSize: {
      metric: '44 - 120',
      imperial: '1.26, 1.73 - 4.72',
    },
    maxFeedSize: {
      metric: '150 - 300',
      imperial: '5.91 - 11.8',
    },
  },
  {
    model: ScreenModels.WS6000H,
    description: 'Ribbtuff',
    material: 'Rubber',
    type: 'Self-supporting',
    function: 'Scalping',
    separationSize: {
      metric: '44 - 120',
      imperial: '1.73 - 4.72',
    },
    maxFeedSize: {
      metric: '250 - 400',
      imperial: '9.84 - 15.8',
    },
  },
  {
    model: ScreenModels.WN4000WN5000,
    description: '',
    material: 'Rubber',
    type: 'Modular',
    function: 'Anti-blinding',
    separationSize: {
      metric: '2 - 25',
      imperial: '0.079 - 0.98',
    },
    maxFeedSize: {
      metric: '20 - 50',
      imperial: '0.787 - 1.97',
    },
  },
  {
    model: ScreenModels.WN6000,
    description: '',
    material: 'Rubber',
    type: 'Modular',
    function: 'Standard',
    separationSize: {
      metric: '8 - 90',
      imperial: '0.315 - 3.54',
    },
    maxFeedSize: {
      metric: '50 - 200',
      imperial: '1.97 - 7.87',
    },
  },
  {
    model: ScreenModels.WN7000H,
    description: '',
    material: 'Rubber',
    type: 'Modular',
    function: 'Heavy duty',
    separationSize: {
      metric: '8 - 90',
      imperial: '0.315 - 3.54',
    },
    maxFeedSize: {
      metric: '50 - 200',
      imperial: '1.97 - 7.87',
    },
  },
  {
    model: ScreenModels.WN8000,
    description: '',
    material: 'PU',
    type: 'Modular',
    function: 'Wet (washing)',
    separationSize: {
      metric: '1 - 34',
      imperial: '0.039 - 1.34',
    },
    maxFeedSize: {
      metric: '20 - 125',
      imperial: '0.787 - 4.92',
    },
  },
];
export const ScreeningMediaDataNormalCalcSA_K_M: ScreeningMediaInterface[] = [
  {
    model: ScreenModels.WS6000,
    description: 'Duratuff',
    material: 'Rubber',
    type: 'Self-supporting',
    function: 'Scalping',
    separationSize: {
      metric: '44 - 120',
      imperial: '1.26, 1.73 - 4.72',
    },
    maxFeedSize: {
      metric: '150 - 300',
      imperial: '5.91 - 11.8',
    },
  },
  {
    model: ScreenModels.WS6000H,
    description: 'Ribbtuff',
    material: 'Rubber',
    type: 'Self-supporting',
    function: 'Scalping',
    separationSize: {
      metric: '44 - 120',
      imperial: '1.73 - 4.72',
    },
    maxFeedSize: {
      metric: '250 - 400',
      imperial: '9.84 - 15.8',
    },
  },
  {
    model: ScreenModels.WN4000WN5000,
    description: '',
    material: 'Rubber',
    type: 'Modular',
    function: 'Anti-blinding',
    separationSize: {
      metric: '2 - 25',
      imperial: '0.079 - 0.98',
    },
    maxFeedSize: {
      metric: '20 - 50',
      imperial: '0.787 - 1.97',
    },
  },
  {
    model: ScreenModels.WN6000,
    description: '',
    material: 'Rubber',
    type: 'Modular',
    function: 'Standard',
    separationSize: {
      metric: '8 - 90',
      imperial: '0.315 - 3.54',
    },
    maxFeedSize: {
      metric: '50 - 200',
      imperial: '1.97 - 7.87',
    },
  },
  {
    model: ScreenModels.WN7000H,
    description: '',
    material: 'Rubber',
    type: 'Modular',
    function: 'Heavy duty',
    separationSize: {
      metric: '8 - 90',
      imperial: '0.315 - 3.54',
    },
    maxFeedSize: {
      metric: '50 - 200',
      imperial: '1.97 - 7.87',
    },
  },
  {
    model: ScreenModels.WN8000,
    description: '',
    material: 'PU',
    type: 'Modular',
    function: 'Wet (washing)',
    separationSize: {
      metric: '1 - 34',
      imperial: '0.039 - 1.34',
    },
    maxFeedSize: {
      metric: '20 - 125',
      imperial: '0.787 - 4.92',
    },
  },
  {
    model: ScreenModels.SCREENEX_40D,
    description: '',
    material: 'Rubber',
    type: 'Modular',
    function: 'Anti-Blinding',
    separationSize: {
      metric: '0.6 - 20',
      imperial: '0.0236 - 0.787',
    },
    maxFeedSize: {
      metric: '40',
      imperial: '1.57',
    },
  },
  {
    model: ScreenModels.SCREENEX_60D,
    description: '',
    material: 'Rubber',
    type: 'Modular',
    function: 'Heavy Duty',
    separationSize: {
      metric: '0.5 - 250',
      imperial: '0.0197 - 9.84',
    },
    maxFeedSize: {
      metric: '600',
      imperial: '23.6',
    },
  },
  {
    model: ScreenModels.SCREENEX_65D,
    description: '',
    material: 'PU',
    type: 'Modular',
    function: 'Anti-Blinding',
    separationSize: {
      metric: '0.5 - 20',
      imperial: '0.0197 - 0.787',
    },
    maxFeedSize: {
      metric: '40',
      imperial: '1.57',
    },
  },
  {
    model: ScreenModels.SCREENEX_80D,
    description: '',
    material: 'PU',
    type: 'Modular',
    function: 'Abrasion',
    separationSize: {
      metric: '0.1 - 250',
      imperial: '0.00394 - 9.84',
    },
    maxFeedSize: {
      metric: '600',
      imperial: '23.6',
    },
  },
];

/**
 * Samma som för circular
 */
export const ScreeningMediaEfficiencyCalc = [
  { value: 'SQUARE', name: 'Square' },
  { value: 'ROUND', name: 'Round' },
  { value: 'SLOTTED_2_1', name: 'Slotted 2:1' },
  { value: 'SLOTTED_3_1', name: 'Slotted 3:1' },
  { value: 'SLOTTED_4_1', name: 'Slotted 4:1 or more' },
];

export const filterModels = (
  models: string[],
  nbrOfDecks: number,
): string[] => {
  if (nbrOfDecks === 1) {
    return models.filter((m) => m.charAt(5) === '1');
  } else if (nbrOfDecks === 2) {
    return models.filter((m) => m.charAt(5) === '2');
  } else {
    return models.filter((m) => m.charAt(5) === '3');
  }
};

export const getScreeningMediaData = (
  screenModel: string,
  _deck: number | undefined,
) => {
  return screenModel.charAt(1) === 'A' &&
    (screenModel.charAt(screenModel.length - 1) === 'K' ||
      screenModel.charAt(screenModel.length - 1) === 'M')
    ? LMScreenConfig.ScreeningMediaDataNormalCalcSA_K_M
    : LMScreenConfig.ScreeningMediaDataNormalCalcSL;
};

/**
 * Exakt samma som för circular
 */
export const OverUnderSizeConstants: {
  name: string;
  value: string;
  visible: boolean;
}[] = [
  { name: 'Not a Product', value: 'NOT_PRODUCT', visible: true },
  { name: 'High', value: 'TEN_TEN', visible: true },
  { name: 'Normal', value: 'TEN_FIFTEEN', visible: true },
  { name: '10/20', value: 'TEN_TWENTY', visible: false },
  { name: 'Low', value: 'FIFTEEN_TWENTY', visible: true },
  { name: '20/25', value: 'TWENTY_TWENTYFIVE', visible: false },
  { name: '20/30', value: 'TWENTY_THIRTY', visible: false },
];

/**
 * samma som för circular motion screen
 */
export const MaterialTypes: { name: string; value: string }[] = [
  { name: 'Normal Crushed Stone', value: 'NORMAL_CRUSHED_STONE' },
  { name: 'Cubical Crushed Stone', value: 'CUBICAL_CRUSHED_STONE' },
  { name: 'Uncrushed Sand and Gravel', value: 'UNCRUSHED_SAND_AND_GRAVEL' },
];

export const AvailableInclineDegrees = (
  screenFamily: LMScreenFamily,
): number[] => {
  return screenFamily === 'SL_EXCITER' || screenFamily === 'SA_LM'
    ? [-3, -2, -1, 0, 1, 2, 3, 4, 5, 6, 7, 8]
    : [0, 5, 10];
};

/**
 * Samma som för circular
 */
export const AvailableParticleShapes = [
  { name: 'Dry Cubical', value: 'DRY_CUBICAL' },
  { name: 'Slabby Elongated', value: 'SLABBY' },
];

/**
 * Samma som för circular
 */
export const AvailableEfficiency = [
  { value: 'CALC', name: 'Calc' },
  { value: 'FULL', name: '100%' },
  { value: 'OTHER', name: 'Other' },
  { value: 'QSPEC', name: 'Qspec' },
];

export const Decks: number[] = [1, 2, 3];

/**
 * Returns the default unitinfo object for a Circular Motion Screen
 */
export const getDefaultData = (): UnitInfoLMScreenInterface => {
  const defaultScreeningMedia: ScreeningMediaInterface =
    ScreeningMediaDataNormalCalcSL[13];

  const defaultDeckDisplay = {
    separationSize: true,
    model: true,
    openArea: false,
    particleShape: false,
    efficiency: false,
    description: false,
    material: false,
    type: false,
    function: false,
    maxFeedSize: false,
  };
  const defaultDecks: LinearMotionScreenDeckMapInterface = {
    0: {
      separationSize: 50,
      maxAmountOfOverUnderSize: OverUnderSizeConstants[2],
      screeningMedia: defaultScreeningMedia,
      screeningElement: ScreeningMediaEfficiencyCalc[0],
      openArea: 50,
      particleShape: AvailableParticleShapes[0],
      efficiency: AvailableEfficiency[0],
      efficiencyNumber: 0,
      display: defaultDeckDisplay,
    },
    1: {
      separationSize: 25,
      maxAmountOfOverUnderSize: OverUnderSizeConstants[2],
      screeningMedia: defaultScreeningMedia,
      screeningElement: ScreeningMediaEfficiencyCalc[0],
      openArea: 50,
      particleShape: AvailableParticleShapes[0],
      efficiency: AvailableEfficiency[0],
      efficiencyNumber: 0,
      display: defaultDeckDisplay,
    },
    2: {
      separationSize: 12,
      maxAmountOfOverUnderSize: OverUnderSizeConstants[2],
      screeningMedia: defaultScreeningMedia,
      screeningElement: ScreeningMediaEfficiencyCalc[0],
      openArea: 50,
      particleShape: AvailableParticleShapes[0],
      efficiency: AvailableEfficiency[0],
      efficiencyNumber: 0,
      display: defaultDeckDisplay,
    },
  };

  return {
    modelFamily: 'SL',
    screenFamily: 'SL',
    sizeOption: '0',
    nbrOfDecks: 2,
    modelName: 'SL1852',
    incline: AvailableInclineDegrees('SL')[1],
    deckList: defaultDecks,
    wetScreening: false,
    parallelUnits: 1,
    deckOneRelieving: false,
    materialType: MaterialTypes[0],
    moisture: 0,
    newMoisture: false,
    display: {
      displayNotes: {
        noteOne: false,
        noteTwo: false,
        noteThree: false,
        noteFour: false,
        noteFive: false,
      },
      displayUnitName: true,
      displayModelName: true,
      displayInclination: true,
      displayLoad: true,
      displayNumberOfDecks: false,
      displayWetScreening: false,
    },
  };
};

export class LinearMotionScreenConfig extends BaseUnitConfig {
  static getDefaultData(): UnitInfoLMScreenInterface {
    const defaultScreeningMedia: ScreeningMediaInterface =
      ScreeningMediaDataNormalCalcSL[13];

    const defaultDeckDisplay = {
      separationSize: true,
      model: true,
      openArea: false,
      particleShape: false,
      efficiency: false,
      description: false,
      material: false,
      type: false,
      function: false,
      maxFeedSize: false,
    };
    const defaultDecks: LinearMotionScreenDeckMapInterface = {
      0: {
        separationSize: 50,
        maxAmountOfOverUnderSize: OverUnderSizeConstants[2],
        screeningMedia: defaultScreeningMedia,
        screeningElement: ScreeningMediaEfficiencyCalc[0],
        openArea: 50,
        particleShape: AvailableParticleShapes[0],
        efficiency: AvailableEfficiency[0],
        efficiencyNumber: 0,
        display: defaultDeckDisplay,
      },
      1: {
        separationSize: 25,
        maxAmountOfOverUnderSize: OverUnderSizeConstants[2],
        screeningMedia: defaultScreeningMedia,
        screeningElement: ScreeningMediaEfficiencyCalc[0],
        openArea: 50,
        particleShape: AvailableParticleShapes[0],
        efficiency: AvailableEfficiency[0],
        efficiencyNumber: 0,
        display: defaultDeckDisplay,
      },
      2: {
        separationSize: 12,
        maxAmountOfOverUnderSize: OverUnderSizeConstants[2],
        screeningMedia: defaultScreeningMedia,
        screeningElement: ScreeningMediaEfficiencyCalc[0],
        openArea: 50,
        particleShape: AvailableParticleShapes[0],
        efficiency: AvailableEfficiency[0],
        efficiencyNumber: 0,
        display: defaultDeckDisplay,
      },
    };

    return {
      modelFamily: 'SL',
      screenFamily: 'SL',
      sizeOption: '0',
      nbrOfDecks: 2,
      modelName: 'SL1852',
      incline: AvailableInclineDegrees('SL')[1],
      deckList: defaultDecks,
      wetScreening: false,
      ...BaseUnitConfig.getBaseDefaults(),
      deckOneRelieving: false,
      materialType: MaterialTypes[0],
      display: {
        ...BaseUnitConfig.getDefaultDisplay(),
        displayInclination: true,
        displayLoad: true,
        displayRiskLevel: false,
        displayNumberOfDecks: false,
        displayWetScreening: false,
      },
    };
  }

  static getNumberOfPorts(): number {
    return 4;
  }
}

export const linearMotionScreenConfig = new LinearMotionScreenConfig();
